/* global __APP_VERSION__ */
/**
 * Gestion du cache navigateur (Cache API, service workers) pour les utilisateurs.
 * - Au déploiement : nouvelle version → vidage + rechargement une fois.
 * - À la déconnexion : vidage pour éviter de servir d’anciens JS/CSS.
 * - Au déploiement également : purge du brouillon/résultat du diagnostic public
 *   (Diagnostic.vue), pour qu'un visiteur revenant après une mise à jour reparte
 *   d'un formulaire vierge plutôt que de restaurer des réponses potentiellement
 *   incompatibles avec la nouvelle version du tunnel.
 */
import { DIAGNOSTIC_DRAFT_KEY, DIAGNOSTIC_STORAGE_KEY } from '@/utils/diagnostic';

const VERSION_STORAGE_KEY = 'patrimonea_app_build_version';

function clearPublicDiagnosticCache() {
  try { window.localStorage.removeItem(DIAGNOSTIC_DRAFT_KEY); } catch (_) {}
  try { window.sessionStorage.removeItem(DIAGNOSTIC_STORAGE_KEY); } catch (_) {}
}

/** @returns {string} */
function getAppVersion() {
  try {
    // Remplacé au build par vue.config.js (DefinePlugin)
    return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0';
  } catch (_) {
    return '1.0.0';
  }
}

/**
 * Supprime les entrées du Cache Storage et désinscrit les service workers.
 * Ne touche pas à localStorage (sauf clé de version gérée ailleurs).
 */
export async function clearBrowserCachesAndServiceWorkers() {
  if (typeof window === 'undefined') return;
  try {
    if (window.caches && typeof window.caches.keys === 'function') {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch (e) {
    console.warn('[patrimonea] caches.delete:', e);
  }
  try {
    if (navigator.serviceWorker && typeof navigator.serviceWorker.getRegistrations === 'function') {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) {
    console.warn('[patrimonea] serviceWorker.unregister:', e);
  }
}

/**
 * Si la version embarquée dans le build a changé depuis la dernière visite,
 * vide les caches et recharge la page une fois (évite les vieux bundles après déploiement).
 * @returns {Promise<boolean>} true = rechargement déclenché, l’appelant ne doit pas monter l’app
 */
export async function maybeRefreshCachesOnNewVersion() {
  if (typeof window === 'undefined') return false;
  const version = getAppVersion();
  let previous = null;
  try {
    previous = window.localStorage.getItem(VERSION_STORAGE_KEY);
  } catch (_) {
    return false;
  }

  if (previous === null) {
    try {
      window.localStorage.setItem(VERSION_STORAGE_KEY, version);
    } catch (_) {}
    return false;
  }

  if (previous === version) return false;

  await clearBrowserCachesAndServiceWorkers();
  clearPublicDiagnosticCache();
  try {
    window.localStorage.setItem(VERSION_STORAGE_KEY, version);
  } catch (_) {}
  window.location.reload();
  return true;
}
